import { useEffect, useMemo, useState } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useSearchParams } from "react-router-dom";
import { BrandsEndPointLogic } from "../../../../../../logic/Partners/DataIntegrations/BrandsEndPointLogic";
import { selectUserToken } from "../../../../../../features/userSlice";
import { useSelector } from "react-redux";
import RawTableDetails from "./RawTableDetails";

import { Paper, Box, Divider, Stack, Typography } from "@mui/material";

import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import dayjs from "dayjs";
import RawTableSkeleton from "../../BrandSkeletons/RawTableSkeleton";

const RawTableOverview = () => {
  const oauthTokenHeader = useSelector(selectUserToken) as string;
  const [searchParams] = useSearchParams("");
  const [loading, setLoading] = useState(false);
  const [hasFetchedRawData, setHasFetchedRawData] = useState(false);

  const [filteredData, setFilteredData] = useState<any[]>([]);

  // Get parameters from URL
  const rawTableIdFromURL = searchParams.get("rawTableId");
  const startDate = searchParams.get("start_date") || "";
  const endDate = searchParams.get("end_date") || "";
  const fieldMappingId = searchParams.get("fieldMappingId") || "";
  const metric = searchParams.get("metric") || "";
  const type = fieldMappingId ? "metric" : "raw";

  const rawTableData = useSelector(
    (state: any) => state.brandConfigurationReport.rawTableData,
  );

  // Dynamically derive tableHeadings from the first row of the API response.

  const tableHeadings = useMemo<Record<string, string>>(() => {
    if (!rawTableData || rawTableData.length === 0) return {};

    return Object.fromEntries(
      Object.keys(rawTableData[0]).map((key) => [key, key]),
    );
  }, [rawTableData]);

  useEffect(() => {
    if (!rawTableIdFromURL || !startDate || !endDate) {
      return;
    }

    // For metric flow, fieldMappingId is required
    if (type === "metric" && !fieldMappingId) {
      return;
    }
    const getRawData = async () => {
      setLoading(true);
      setHasFetchedRawData(false);

      try {
        await BrandsEndPointLogic.getRawTableData(
          oauthTokenHeader,
          rawTableIdFromURL,
          startDate,
          endDate,
          type,
          fieldMappingId ? fieldMappingId : undefined,
        );
      } catch (e: any) {
        console.error(e);
      } finally {
        setLoading(false);
        setHasFetchedRawData(true);
      }
    };

    getRawData();
    // eslint-disable-next-line
  }, [rawTableIdFromURL, startDate, endDate, fieldMappingId]);

  const handleDownloadCSV = () => {
    if (!filteredData?.length) return;

    const headers = Object.keys(filteredData[0]);
    const rows = filteredData.map((row: any) =>
      headers.map((header) => {
        const value = row[header];
        if (value === null || value === undefined) return "";
        return String(value).replace(/"/g, '""');
      }),
    );

    const csvContent = [
      headers.join(","),
      ...rows.map((row: any) => row.map((cell: any) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `raw_table_${rawTableIdFromURL}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <>
      {!hasFetchedRawData ? (
        <RawTableSkeleton />
      ) : (
        <Paper sx={{ padding: "16px 24px 20px 24px" }}>
          {/* ── Header ── */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={0.5}
          >
            <Typography variant="h5" fontWeight={500} color="text.primary">
              {metric && (
                <>
                  <Box component="span" sx={{ color: "#9C27B0" }}>
                    {metric}
                  </Box>
                  <Box component="span"> from</Box>
                </>
              )}
              Table{" "}
              <Box component="span" sx={{ color: "#9C27B0" }}>
                {rawTableIdFromURL ?? ""}
              </Box>
            </Typography>

            <Tooltip
              title={
                loading
                  ? "Loading..."
                  : filteredData?.length === 0
                    ? "No data available"
                    : "Download CSV"
              }
            >
              <span>
                <IconButton
                  onClick={handleDownloadCSV}
                  disabled={
                    loading || filteredData?.length === 0 || !hasFetchedRawData
                  }
                  color="secondary"
                >
                  <DownloadIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          {/* ── Date range ── */}
          {(startDate || endDate) && (
            <Stack direction="row" alignItems="center" gap={1} mb={2}>
              <CalendarTodayOutlinedIcon
                sx={{ fontSize: 14, color: "text.secondary" }}
              />
              <Typography variant="body2" color="text.secondary">
                {dayjs(startDate).format("DD/MM/YYYY")} –{" "}
                {dayjs(endDate).format("DD/MM/YYYY")}
              </Typography>
            </Stack>
          )}

          <Divider sx={{ mb: 2 }} />

          <RawTableDetails
            tableHeadings={tableHeadings}
            onFilteredDataChange={setFilteredData}
          />
        </Paper>
      )}
    </>
  );
};

export default RawTableOverview;
